import { icon } from '../../icons';
import { type BillingOverviewDto } from '../../services/billing.api';
import { type DocumentDto } from '../../services/api';
import { t, translateDocumentType } from '../../services/i18n';
import {
  esc,
  formatBytes,
  formatDate,
  getFilteredDocuments,
  getPlanName,
  isExpired,
  isExpiringSoon,
  renderEmptyMessage,
  renderStorageField,
} from './document-shared';

export class RcDocumentListView extends HTMLElement {
  documents: DocumentDto[] = [];
  filterType = '';
  billingOverview: BillingOverviewDto | null = null;

  render(): void {
    const docs = getFilteredDocuments(this.documents, this.filterType);
    const storageUsage = this.billingOverview?.storageUsage;

    this.innerHTML = `
      <div class="page-header">
        <h1>${t('document.title')}</h1>
        <button class="btn btn-primary" data-action="add">
          ${icon('plus', { size: 16, strokeWidth: 2.5 })}
          ${t('document.upload')}
        </button>
      </div>

      ${storageUsage ? `
        <div class="card billing-card document-storage-card">
          <div class="profile-section-head">
            <h2>${t('document.storageTitle')}</h2>
            <p>${t('document.storageHelp')}</p>
          </div>
          <div class="billing-summary-grid">
            ${renderStorageField(t('billing.planLabel'), esc(getPlanName(this.billingOverview)))}
            ${renderStorageField(t('billing.storageUsed'), formatBytes(storageUsage.usedBytes))}
            ${renderStorageField(t('billing.storageAvailable'), formatBytes(storageUsage.limitBytes))}
            ${renderStorageField(t('billing.storageRemaining'), formatBytes(storageUsage.remainingBytes))}
          </div>
        </div>
      ` : ''}

      <div class="filter-bar">
        <button class="filter-btn ${!this.filterType ? 'active' : ''}" data-filter="">${t('document.all')} (${this.documents.length})</button>
        ${['Epc', 'Inspection', 'Insurance', 'Invoice', 'Photo', 'Other'].map((type) => {
          const count = this.documents.filter((document) => document.documentType === type).length;
          return count > 0
            ? `<button class="filter-btn ${this.filterType === type ? 'active' : ''}" data-filter="${type}">${translateDocumentType(type)} (${count})</button>`
            : '';
        }).join('')}
      </div>

      ${docs.length === 0
        ? `<div class="empty-state card">
            ${icon('file-text', { size: 48, strokeWidth: 1.5, attrs: 'opacity="0.4"' })}
            <p>${renderEmptyMessage(this.filterType)}</p>
            ${!this.filterType ? `<button class="btn btn-primary" data-action="add">${t('document.uploadFirst')}</button>` : ''}
          </div>`
        : `<div class="card table-wrapper">
            <table>
              <thead>
                <tr><th>${t('common.file')}</th><th>${t('common.type')}</th><th>${t('common.linkedTo')}</th><th>${t('common.expiryDate')}</th><th>${t('common.tags')}</th><th>${t('common.added')}</th><th>${t('common.actions')}</th></tr>
              </thead>
              <tbody>
                ${docs.map((document) => {
                  const expiredClass = isExpired(document.expiryDate) ? 'expiry-expired' : isExpiringSoon(document.expiryDate) ? 'expiry-soon' : '';
                  const linkedTo = document.tenantName
                    ? `<div class="linked-to linked-to--tenant">${icon('user', { size: 12 })}${esc(document.tenantName)}</div>`
                    : document.propertyAddress
                      ? `<div class="linked-to linked-to--property">${icon('home', { size: 12 })}${esc(document.propertyAddress)}</div>`
                      : `<span class="text-muted">${t('common.none')}</span>`;
                  return `
                    <tr>
                      <td>
                        <div class="doc-filename">${esc(document.fileName)}</div>
                        <div class="text-muted text-sm">${document.contentType}</div>
                      </td>
                      <td><span class="badge badge-info">${translateDocumentType(document.documentType)}</span></td>
                      <td>${linkedTo}</td>
                      <td class="${expiredClass}">
                        ${document.expiryDate ? `
                          ${formatDate(document.expiryDate)}
                          ${isExpired(document.expiryDate) ? `<span class="expiry-badge badge-danger">${t('document.expired')}</span>` : ''}
                          ${isExpiringSoon(document.expiryDate) ? `<span class="expiry-badge badge-warning">${t('document.expiringSoon')}</span>` : ''}
                        ` : '-'}
                      </td>
                      <td>${document.tags ? esc(document.tags) : '-'}</td>
                      <td>${formatDate(document.createdAt)}</td>
                      <td>
                        <div class="card-actions">
                          <button class="btn btn-outline btn-sm" data-action="download" data-id="${document.id}">${t('common.download')}</button>
                          <button class="btn btn-outline btn-sm" data-action="share" data-id="${document.id}">${t('document.share')}</button>
                          <button class="btn btn-danger btn-sm" data-action="delete" data-id="${document.id}">${t('common.delete')}</button>
                        </div>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>`}
    `;

    this.querySelectorAll<HTMLElement>('[data-action="add"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('document-add', { bubbles: true })));
    });
    this.querySelectorAll<HTMLElement>('[data-action="download"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('document-download', {
        bubbles: true,
        detail: { id: button.dataset.id },
      })));
    });
    this.querySelectorAll<HTMLElement>('[data-action="share"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('document-share', {
        bubbles: true,
        detail: { id: button.dataset.id },
      })));
    });
    this.querySelectorAll<HTMLElement>('[data-action="delete"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('document-delete', {
        bubbles: true,
        detail: { id: button.dataset.id },
      })));
    });
    this.querySelectorAll<HTMLElement>('.filter-btn').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('document-filter-change', {
        bubbles: true,
        detail: { filterType: button.dataset.filter || '' },
      })));
    });
  }
}

if (!customElements.get('rc-document-list-view')) {
  customElements.define('rc-document-list-view', RcDocumentListView);
}
